<!-- This component was generated using AI + some modifications -->
<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'

interface Option {
  value: string
  label: string
}

const props = defineProps<{
  modelValue: string[]
  options: Option[]
  placeholder?: string
  showSearch?: boolean
  maxDisplay?: number
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string[]]
}>()

const isOpen = ref(false)
const searchQuery = ref('')
const dropdownRef = ref<HTMLElement | null>(null)
const buttonRef = ref<HTMLElement | null>(null)
const searchInputRef = ref<HTMLInputElement | null>(null)
const focusedIndex = ref(-1)

const filteredOptions = computed(() => {
  if (!searchQuery.value) return props.options

  const query = searchQuery.value.toLowerCase()
  return props.options.filter((option) => option.label.toLowerCase().includes(query))
})

const displayText = computed(() => {
  if (props.modelValue.length === 0) {
    return props.placeholder || 'Select options...'
  }

  const maxDisplay = props.maxDisplay || 2
  if (props.modelValue.length <= maxDisplay) {
    return props.modelValue
      .map((val) => props.options.find((opt) => opt.value === val)?.label)
      .filter(Boolean)
      .join(', ')
  }

  return `${props.modelValue.length} selected`
})

function toggleDropdown() {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    searchQuery.value = ''
    focusedIndex.value = -1
    nextTick(() => {
      if (props.showSearch && searchInputRef.value) {
        searchInputRef.value.focus()
      }
    })
  }
}

function toggleOption(value: string) {
  const currentValues = [...props.modelValue]
  const index = currentValues.indexOf(value)

  if (index > -1) {
    currentValues.splice(index, 1)
  } else {
    currentValues.push(value)
  }

  emit('update:modelValue', currentValues)
}

function handleClickOutside(event: MouseEvent) {
  if (
    dropdownRef.value &&
    !dropdownRef.value.contains(event.target as Node) &&
    buttonRef.value &&
    !buttonRef.value.contains(event.target as Node)
  ) {
    isOpen.value = false
  }
}

function handleKeyDown(event: KeyboardEvent) {
  if (!isOpen.value) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      toggleDropdown()
    }
    return
  }

  switch (event.key) {
    case 'Escape':
      event.preventDefault()
      isOpen.value = false
      buttonRef.value?.focus()
      break

    case 'ArrowDown':
      event.preventDefault()
      focusedIndex.value = Math.min(focusedIndex.value + 1, filteredOptions.value.length - 1)
      scrollToFocusedOption()
      break

    case 'ArrowUp':
      event.preventDefault()
      focusedIndex.value = Math.max(focusedIndex.value - 1, 0)
      scrollToFocusedOption()
      break

    case 'Enter':
    case ' ':
      event.preventDefault()
      if (focusedIndex.value >= 0 && focusedIndex.value < filteredOptions.value.length) {
        const fo = filteredOptions.value
        const option = fo[focusedIndex.value]
        if (option) toggleOption(option.value)
      }
      break

    case 'Tab':
      isOpen.value = false
      break
  }
}

function scrollToFocusedOption() {
  nextTick(() => {
    const focusedElement = dropdownRef.value?.querySelector(`[data-index="${focusedIndex.value}"]`)
    if (focusedElement) {
      focusedElement.scrollIntoView({ block: 'nearest' })
    }
  })
}

function handleOptionClick(value: string, index: number) {
  toggleOption(value)
  focusedIndex.value = index
}

function selectAll() {
  emit(
    'update:modelValue',
    filteredOptions.value.map((opt) => opt.value),
  )
}

function clearAll() {
  emit('update:modelValue', [])
}

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('mousedown', handleClickOutside)
})
</script>

<template>
  <div class="relative w-full">
    <!-- Trigger Button -->
    <button
      ref="buttonRef"
      type="button"
      @click="toggleDropdown"
      @keydown="handleKeyDown"
      class="px-4 py-2 text-left bg-white border border-grey rounded-lg cursor-pointer hover:border-black focus:outline-none focus:ring-2 focus:ring-green focus:border-transparent transition-colors"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      :aria-label="placeholder || 'Select options'"
    >
      <div class="flex items-center justify-between min-w-32">
        <span class="truncate" :class="{ 'text-grey': modelValue.length === 0 }">
          {{ displayText }}
        </span>
        <svg
          class="w-5 h-5 ml-2 transition-transform"
          :class="{ 'rotate-180': isOpen }"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>
    </button>

    <!-- Dropdown Menu -->
    <div
      v-if="isOpen"
      ref="dropdownRef"
      class="absolute z-50 w-full mt-1 bg-white border border-grey rounded-lg shadow-lg"
      role="listbox"
      :aria-label="placeholder || 'Options list'"
    >
      <!-- Search Input -->
      <div v-if="showSearch" class="p-2 border-b border-grey">
        <input
          ref="searchInputRef"
          v-model="searchQuery"
          type="text"
          placeholder="Search..."
          class="w-full px-3 py-2 border border-grey rounded-lg focus:outline-none focus:ring-2 focus:ring-green focus:border-transparent"
          @keydown="handleKeyDown"
        />
      </div>

      <!-- Action Buttons -->
      <div v-if="options.length > 0" class="flex justify-between p-2 border-b border-grey">
        <button
          type="button"
          @click="selectAll"
          class="text-sm text-green hover:underline focus:outline-none focus:underline"
        >
          Select All
        </button>
        <button
          type="button"
          @click="clearAll"
          class="text-sm text-green hover:underline focus:outline-none focus:underline"
        >
          Clear All
        </button>
      </div>

      <!-- Options List -->
      <div class="max-h-80 overflow-y-auto">
        <template v-if="filteredOptions.length > 0">
          <label
            v-for="(option, index) in filteredOptions"
            :key="option.value"
            :data-index="index"
            class="flex items-center px-4 py-2 cursor-pointer hover:bg-grey/20 transition-colors"
            :class="{
              'bg-grey/10': index === focusedIndex,
            }"
            @click.prevent="handleOptionClick(option.value, index)"
            @mouseenter="focusedIndex = index"
          >
            <input
              type="checkbox"
              :value="option.value"
              :checked="modelValue.includes(option.value)"
              class="mr-3 w-4 h-4 rounded border-grey text-green focus:ring-green cursor-pointer pointer-events-none"
              :aria-label="option.label"
              tabindex="-1"
              readonly
            />
            <span class="select-none">{{ option.label }}</span>
          </label>
        </template>
        <div v-else class="px-4 py-3 text-center text-grey">No options found</div>
      </div>

      <!-- Selected Count Footer -->
      <div
        v-if="modelValue.length > 0"
        class="px-4 py-2 border-t border-grey bg-grey/5 text-sm text-grey"
      >
        {{ modelValue.length }} item{{ modelValue.length !== 1 ? 's' : '' }} selected
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Custom scrollbar styling */
.overflow-y-auto::-webkit-scrollbar {
  width: 8px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
